import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ExternalLink, ArrowRight, RefreshCw, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PortalPreview() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [build, setBuild] = useState(null);
  const [error, setError] = useState(null);
  const [polling, setPolling] = useState(true);

  useEffect(() => {
    fetchBuild();
  }, [leadId]);

  useEffect(() => {
    // Poll for updates if still generating
    if (polling && build?.lead?.status === "generating") {
      const interval = setInterval(fetchBuild, 3000);
      return () => clearInterval(interval);
    }
  }, [polling, build?.lead?.status]);

  const fetchBuild = async () => {
    try {
      const response = await axios.get(`${API}/portal/build/${leadId}`, {
        withCredentials: true
      });
      setBuild(response.data);
      
      // Stop polling if preview is ready or error
      if (response.data.lead?.portal_status !== "draft" && 
          response.data.lead?.status !== "generating") {
        setPolling(false);
      }
    } catch (error) {
      console.error("Failed to fetch build:", error);
      if (error.response?.status === 401) {
        navigate("/");
      } else {
        setError(error.response?.data?.detail || "Failed to load build");
      }
      setPolling(false);
    } finally {
      setLoading(false);
    }
  };

  const handleContinueToCheckout = () => {
    navigate(`/portal/checkout/${leadId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="animate-spin mx-auto mb-4" size={40} />
          <p className="text-muted-foreground">Loading your preview...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <h2 className="text-xl font-semibold text-red-600 mb-2">Error</h2>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={() => navigate("/")}>Go Back</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isGenerating = build?.lead?.status === "generating" || build?.lead?.portal_status === "draft";
  const previewUrl = build?.lead?.preview_url;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_eff51d80-5789-419f-a2d8-ecfb7c53e41f/artifacts/rtlggz3k_Invigor%20Digital%20logo.png" 
              alt="Invigor Digital" 
              className="h-10 w-auto"
            />
          </div>
          <div className="flex items-center gap-4">
            {previewUrl && (
              <a 
                href={`${process.env.REACT_APP_BACKEND_URL}${previewUrl}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button variant="outline" size="sm">
                  <ExternalLink size={16} className="mr-2" />
                  Open in New Tab
                </Button>
              </a>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Status Bar */}
        <Card className="mb-6">
          <CardContent className="py-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-xl font-bold">{build?.lead?.business_name}</h1>
                <p className="text-muted-foreground">
                  {build?.lead?.city}, {build?.lead?.state} • {build?.lead?.primary_industry}
                </p>
              </div>
              
              {isGenerating ? (
                <div className="flex items-center gap-2 text-blue-600">
                  <Loader2 className="animate-spin" size={20} />
                  <span>Generating your preview...</span>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle size={20} />
                    <span>Preview Ready</span>
                  </div>
                  <Button onClick={handleContinueToCheckout} data-testid="portal-checkout-btn">
                    Approve & Checkout
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Preview Frame */}
        {isGenerating ? (
          <Card>
            <CardContent className="py-16 text-center">
              <Loader2 className="animate-spin mx-auto mb-4" size={48} />
              <h2 className="text-xl font-semibold mb-2">Building Your Website Preview</h2>
              <p className="text-muted-foreground mb-4">
                This usually takes 30-60 seconds. Please wait...
              </p>
              <Button variant="outline" onClick={fetchBuild}>
                <RefreshCw size={16} className="mr-2" />
                Check Status
              </Button>
            </CardContent>
          </Card>
        ) : previewUrl ? (
          <Card className="overflow-hidden">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Homepage Preview</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="border-t">
                <iframe
                  src={`${process.env.REACT_APP_BACKEND_URL}${previewUrl}`}
                  title="Website Preview"
                  className="w-full h-[600px] border-none"
                />
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="py-16 text-center">
              <h2 className="text-xl font-semibold mb-2">Preview Not Available</h2>
              <p className="text-muted-foreground">
                There was an issue generating your preview. Please try again.
              </p>
              <Button className="mt-4" onClick={() => navigate("/portal/build")}>
                Start Over
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Info Section */}
        {!isGenerating && previewUrl && (
          <div className="mt-6 grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">What happens next?</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground space-y-2">
                <p>1. Review your preview above</p>
                <p>2. Click &quot;Approve &amp; Checkout&quot; when you are ready</p>
                <p>3. Complete payment ($600 for your complete website)</p>
                <p>4. Download your website files (available for 7 days)</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Your website includes</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground space-y-2">
                <p>✓ Professional homepage design</p>
                <p>✓ Services pages for each offering</p>
                <p>✓ Service area pages for local SEO</p>
                <p>✓ About page and Contact form</p>
                <p>✓ Mobile-responsive design</p>
                <p>✓ SEO-optimized content</p>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}
